import React, { useState, useEffect } from 'react';
import {
  Eye, EyeOff, ShieldCheck, Smartphone, ArrowLeft, ArrowRight
} from 'lucide-react';
import AlerteConnexion from './connexion/AlerteConnexion';
import WelcomeScreen from './WelcomeScreen';

import PasswordChangeModal from './PasswordChangeModal';
import EtablissementIndisponible from './EtablissementIndisponible';
import KiosqueWelcomeScreen from './KiosqueWelcomeScreen';
import CryptoJS from 'crypto-js';
import { api, getAuthHeaders, getEntetesConnexion } from '../services/api';
import SceneEcole from './connexion/SceneEcole';
import PopupConnexion from './connexion/PopupConnexion';
import { variables as variablesCouleurs } from './connexion/couleursConnexion';
import { noterRecouvrementDisponible, recouvrementDisponibleDepuisAnnees } from '../utils/recouvrementDisponible';
import {
  CONTENUS_PAR_DEFAUT, fusionner, lireCache, ecrireCache, chargerContenus
} from './connexion/contenusConnexion';

// ============================================================================
// PAGE DE CONNEXION — version 2 (modèle « Campus, connexion fondue »)
// ============================================================================
//
// La version précédente est archivée dans
// `components/dump/ConnexionPage-V1-avant-refonte.js`.
//
// Ce qui change :
//   * les textes — marque, textes défilants, bloc de connexion, pied de page,
//     pop-ups — se règlent depuis le CIK (« Page de connexion KLASYS ») ;
//   * le nom de l'établissement s'affiche d'après le sous-domaine ;
//   * l'image est un dessin vectoriel écrit dans le code : aucun écran noir au
//     rechargement ;
//   * « Mot de passe oublié » ouvre une pop-up au lieu d'un lien mort.
//
// Ce qui ne change pas : la logique de connexion. Se souvenir de moi, les trois
// états d'année, la double authentification, le changement de mot de passe à
// la première connexion, licence expirée, accès suspendu, établissement
// indisponible, écrans d'accueil et de kiosque.
// ============================================================================

// ============================================================================
// Cryptage pour "Se souvenir de moi"
// ============================================================================
const SECRET_KEY = 'klasys-secret-key-2025';

const encryptPassword = (password) => {
  return CryptoJS.AES.encrypt(password, SECRET_KEY).toString();
};

const decryptPassword = (encryptedPassword) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedPassword, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return '';
  }
};

const POLICE_TITRE = { fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif" };

// Les trois états d'une année, et ce que chacun permet.
const ETATS_ANNEE = {
  active: {
    libelle: 'Active',
    note: 'Année en cours : toutes vos modifications sont enregistrées.',
    classes: 'bg-[color:var(--connexion-pale)] text-[color:var(--connexion-foncee)]'
  },
  cloturee: {
    libelle: 'Clôturée',
    note: 'Cette année est clôturée : consultation seule, aucune modification ne sera enregistrée.',
    classes: 'bg-amber-50/90 text-amber-800'
  },
  a_venir: {
    libelle: 'À venir',
    note: 'Année à venir : préparez classes, tarifs et calendrier avant la rentrée.',
    classes: 'bg-teal-50/90 text-teal-800'
  }
};

const ConnexionPage = () => {
  // États du formulaire
  const [formData, setFormData] = useState({
    identifiant: '',
    mot_de_passe: '',
    annee_scolaire_id: ''
  });

  // États UI
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // Code renvoyé par le serveur quand l'adresse ne mène à aucun établissement
  // utilisable : TENANT_NOT_FOUND ou TENANT_SUSPENDED.
  const [etablissementIndisponible, setEtablissementIndisponible] = useState(null);
  const [showKiosqueWelcome] = useState(false);

  // États données
  const [years, setYears] = useState([]);
  const [error, setError] = useState({
    show: false,
    message: ''
  });

  // ==========================================================================
  // Contenus réglés depuis le CIK
  // ==========================================================================
  // Premier rendu : la dernière réponse gardée pour cette école, à défaut les
  // valeurs du code. La réponse du serveur remplace ensuite sans bruit.
  const [contenus, setContenus] = useState(() => {
    const cache = lireCache();
    return fusionner(cache?.config || {});
  });
  const [nomEcole, setNomEcole] = useState(() => lireCache()?.ecole?.nom || null);
  const [popupOuverte, setPopupOuverte] = useState(null);

  useEffect(() => {
    let actif = true;
    chargerContenus().then((donnees) => {
      if (!actif || !donnees) return;
      setContenus(fusionner(donnees.config || {}));
      setNomEcole(donnees.ecole?.nom || null);
      ecrireCache(donnees);
    });
    return () => { actif = false; };
  }, []);

  // Lien vers l'espace famille, seulement si l'école l'a ouvert (licence
  // `module.parents`) : ailleurs, il mènerait à une page « pas encore ouvert ».
  const [espaceFamilleOuvert, setEspaceFamilleOuvert] = useState(false);
  useEffect(() => {
    let actif = true;
    fetch('/api/famille/etat', { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((corps) => { if (actif) setEspaceFamilleOuvert(Boolean(corps?.data?.ouvert)); })
      .catch(() => {});
    return () => { actif = false; };
  }, []);

  // Même chose pour l'espace enseignant (licence `module.portail_enseignant`).
  const [espaceEnseignantOuvert, setEspaceEnseignantOuvert] = useState(false);
  useEffect(() => {
    let actif = true;
    fetch('/api/espace-enseignant/etat', { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((corps) => { if (actif) setEspaceEnseignantOuvert(Boolean(corps?.data?.ouvert)); })
      .catch(() => {});
    return () => { actif = false; };
  }, []);

  const selectedYear = years.find(
    (year) => year.id?.toString() === formData.annee_scolaire_id?.toString()
  );
  // Trois etats, trois libelles.
  //
  // Une annee ni active ni cloturee est l'annee A VENIR : celle qu'on prepare —
  // classes, tarifs, calendrier. Seule une annee CLOTUREE est une archive.
  const etatAnnee = (annee) => {
    if (!annee) return null;
    if (Number(annee.est_cloturee) === 1) return 'cloturee';
    if (Number(annee.est_active) === 1) return 'active';
    return 'a_venir';
  };
  const selectedYearIsReadOnly = etatAnnee(selectedYear) === 'cloturee';

  // États pour le changement de mot de passe
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [, setCurrentUser] = useState(null);

  // ==========================================================================
  // Rotation des textes
  // ==========================================================================
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const nombreTextes = contenus.textes.length;

  useEffect(() => {
    if (nombreTextes <= 1) return undefined;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % nombreTextes);
        setIsAnimating(false);
      }, 450);
    }, 5000);
    return () => clearInterval(interval);
  }, [nombreTextes]);

  const texteCourant = contenus.textes[currentTextIndex % nombreTextes] || CONTENUS_PAR_DEFAUT.textes[0];

  // Chargement des années scolaires
  useEffect(() => {
    const fetchSchoolYears = async () => {
      try {
        const result = await api.anneesScolaires.getLoginOptions();

        // Une école inexistante ou suspendue n'est pas une panne : le serveur
        // le dit par un code stable, et l'écran de connexion n'a plus lieu
        // d'être.
        if (result.code === 'TENANT_NOT_FOUND' || result.code === 'TENANT_SUSPENDED') {
          setEtablissementIndisponible(result.code);
          return;
        }

        if (result.success && Array.isArray(result.data) && result.data.length > 0) {
          const availableYears = result.data;
          // L'année ACTIVE d'abord, toujours — même si la dernière connexion
          // s'est faite sur une autre : se connecter par mégarde à une année
          // clôturée donne des erreurs inutiles. L'année mémorisée ne sert
          // qu'à défaut d'année active, puis une année non clôturée.
          const savedSchoolYear = localStorage.getItem('rememberedSchoolYear');
          const anneeActive = availableYears.find((year) => Number(year.est_active) === 1);
          const anneeMemorisee = availableYears.find(
            (year) => year.id?.toString() === savedSchoolYear?.toString()
          );
          const anneeOuverte = availableYears.find((year) => Number(year.est_cloturee) !== 1);
          const defaultYear = (anneeActive || anneeMemorisee || anneeOuverte || availableYears[0]).id;

          setYears(availableYears);

          setFormData(prev => ({
            ...prev,
            annee_scolaire_id: defaultYear?.toString() || ''
          }));
        } else {
          setError({
            show: true,
            message: 'Aucune année scolaire disponible'
          });
        }
      } catch (error) {
        console.error('Erreur:', error);
        setError({
          show: true,
          message: 'Erreur lors du chargement des années scolaires'
        });
      }
    };

    fetchSchoolYears();
  }, []);

  // Charger les credentials sauvegardés (cryptés)
  useEffect(() => {
    const savedIdentifiant = localStorage.getItem('rememberedIdentifiant');
    const savedPassword = localStorage.getItem('rememberedPassword');
    const savedRemember = localStorage.getItem('rememberMe');

    if (savedRemember === 'true' && savedIdentifiant) {
      // L'année n'est pas restaurée ici : c'est le chargement des années qui
      // la choisit (l'active d'abord). La restaurer écrasait ce choix.
      setFormData((prev) => ({
        ...prev,
        identifiant: savedIdentifiant,
        mot_de_passe: savedPassword ? decryptPassword(savedPassword) : ''
      }));
      setRememberMe(true);
    }
  }, []);

  // Renvoyé ici par une suspension d'accès : on dit pourquoi.
  useEffect(() => {
    try {
      const motif = sessionStorage.getItem('klasysMotifDeconnexion');
      if (motif) {
        sessionStorage.removeItem('klasysMotifDeconnexion');
        setError({ show: true, titre: 'Vous avez été déconnecté', message: motif, type: 'warning' });
      }
    } catch {
      /* stockage indisponible */
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // ==========================================================================
  // Double authentification
  // ==========================================================================
  // Quand l'ecole l'exige pour un compte, /login ne renvoie pas de jeton mais
  // un DEFI : la page passe a une seconde etape -- saisir le code, ou enroler
  // un telephone la premiere fois. Le defi vaut cinq minutes et cinq essais.
  const [etapeDouble, setEtapeDouble] = useState(null); // { type, defi, qr, secret, identifiant }
  const [codeSaisi, setCodeSaisi] = useState('');
  const [secretVisible, setSecretVisible] = useState(false);

  const abandonnerDouble = () => {
    setEtapeDouble(null);
    setCodeSaisi('');
    setSecretVisible(false);
  };

  // Ce que la page fait d'une reponse de connexion, qu'elle vienne du mot de
  // passe ou du code : les deux doivent ouvrir la session de la meme facon.
  const traiterReponseConnexion = (response, data) => {
    if (response.status === 409 && data.code === 'EXISTING_SESSION_DETECTED') {
      setError({
        show: true,
        titre: 'Session déjà active',
        message: `${data.message}\n\nNombre de sessions actives : ${data.data?.sessionCount || 1}\n\nVeuillez vous déconnecter de l'autre appareil ou utiliser cette session.`,
        type: 'warning'
      });
      setIsLoading(false);
      return;
    }

    // L'ecole exige un second facteur : le mot de passe est bon, mais la
    // connexion n'est pas terminee. Rien n'est ecrit dans le navigateur tant
    // que le code n'est pas passe.
    if (response.ok && data.success && data.deuxiemeFacteur) {
      setEtapeDouble({
        type: data.deuxiemeFacteur,
        defi: data.defi,
        qr: data.qr || null,
        secret: data.secret || null,
        identifiant: data.identifiant || formData.identifiant
      });
      setCodeSaisi('');
      setSecretVisible(false);
      return;
    }

    if (response.ok && data.success) {
      setEtapeDouble(null);
      if (!data.token) {
        console.error('Pas de token dans la réponse!');
        setError({
          show: true,
          message: 'Erreur d\'authentification: token manquant'
        });
        return;
      }

      localStorage.setItem('token', data.token);
      const loginYear = data.user?.annee_scolaire || selectedYear || null;
      const isYearReadOnly = data.user?.annee_scolaire_read_only ?? selectedYearIsReadOnly;
      localStorage.setItem('annee_scolaire_id', formData.annee_scolaire_id);
      localStorage.setItem('annee_scolaire_nom', loginYear?.nom || '');
      localStorage.setItem('annee_scolaire_read_only', isYearReadOnly ? 'true' : 'false');
      // L'onglet Recouvrement se décide ici, avant d'entrer en comptabilité :
      // la liste des années est déjà là (utils/recouvrementDisponible.js).
      if (years.length) noterRecouvrementDisponible(recouvrementDisponibleDepuisAnnees(years));

      // Sauvegarder/Supprimer credentials cryptés
      if (rememberMe) {
        localStorage.setItem('rememberedIdentifiant', formData.identifiant);
        localStorage.setItem('rememberedPassword', encryptPassword(formData.mot_de_passe));
        localStorage.setItem('rememberedSchoolYear', formData.annee_scolaire_id);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberedIdentifiant');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberedSchoolYear');
        localStorage.removeItem('rememberMe');
      }

      if (data.user && data.user.first_login) {
        setCurrentUser(data.user);
        setShowPasswordModal(true);
        setIsLoading(false);
      } else {
        // Stocker les infos utilisateur
        localStorage.setItem('user', JSON.stringify({
          ...data.user,
          annee_scolaire_active: formData.annee_scolaire_id,
          annee_scolaire: loginYear,
          annee_scolaire_read_only: isYearReadOnly
        }));
        localStorage.setItem('isLoggedInNew', 'true');

        if (data.permissions) {
          localStorage.setItem('userPermissions', JSON.stringify(data.permissions));
        }

        // Kiosque de pointage, ou administration
        const rawRedirectTo = data.redirectTo || '/administration';
        const redirectTo = rawRedirectTo === '/admin' ? '/administration' : rawRedirectTo;
        const hasKioskPermission = data.permissions?.some(p =>
          p.permission_key === 'POINTAGES.KIOSK.VIEW'
        );
        const hasOnlyPointagePermissions = data.permissions?.every(p =>
          p.module === 'POINTAGES'
        );
        const isKiosqueUser = hasKioskPermission && hasOnlyPointagePermissions;

        if (isKiosqueUser) {
          window.location.href = '/kiosque-welcome';
          return;
        }

        localStorage.setItem('redirectTo', redirectTo);
        setShowWelcome(true);
      }

    } else if (data.code === 'LICENCE_EXPIREE') {
      // Ce n'est pas une erreur de saisie : l'utilisateur a probablement
      // raison sur ses identifiants.
      setError({
        show: true,
        message: data.message,
        type: 'error'
      });
    } else {
      setError({
        show: true,
        message: data.message || 'Identifiants incorrects',
        type: 'error'
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError({ show: false, message: '' });

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: getEntetesConnexion(),
        credentials: 'include',
        body: JSON.stringify({
          identifiant: formData.identifiant,
          mot_de_passe: formData.mot_de_passe,
          annee_scolaire_id: formData.annee_scolaire_id
        })
      });

      const data = await response.json();
      traiterReponseConnexion(response, data);
    } catch (error) {
      console.error('Erreur complète:', error);
      setError({
        show: true,
        message: 'Erreur de connexion au serveur',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Seconde etape : le code, ou l'enrolement d'un telephone.
  const handleCode = async (e) => {
    e.preventDefault();
    if (!etapeDouble) return;

    const code = codeSaisi.replace(/\s/g, '');
    if (!/^\d{6}$/.test(code)) {
      setError({ show: true, message: 'Le code doit comporter six chiffres.', type: 'error' });
      return;
    }

    setIsLoading(true);
    setError({ show: false, message: '' });

    try {
      const route = etapeDouble.type === 'enrolement'
        ? '/api/auth/login/enrolement'
        : '/api/auth/login/code';
      const response = await fetch(route, {
        method: 'POST',
        headers: getEntetesConnexion(),
        credentials: 'include',
        body: JSON.stringify({ defi: etapeDouble.defi, code })
      });
      const data = await response.json();

      // Delai depasse, essais epuises, compte change entre-temps : le defi
      // ne vaut plus rien. Retour au formulaire, qui garde ce qui etait saisi.
      if (!response.ok && data.code === 'DEFI_INVALIDE') {
        abandonnerDouble();
        setError({ show: true, message: data.message, type: 'error' });
        return;
      }

      // Code faux : on reste sur l'etape, champ vide.
      if (!response.ok && data.code !== 'LICENCE_EXPIREE') {
        setCodeSaisi('');
        setError({ show: true, message: data.message || 'Code incorrect', type: 'error' });
        return;
      }

      traiterReponseConnexion(response, data);
    } catch (error) {
      console.error('Erreur second facteur:', error);
      setError({ show: true, message: 'Erreur de connexion au serveur', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (newPassword) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/first-login-change-password', {
        method: 'POST',
        headers: getEntetesConnexion({ avecJeton: true }),
        body: JSON.stringify({ nouveau_mot_de_passe: newPassword })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setShowPasswordModal(false);

        setError({
          show: true,
          message: 'Mot de passe mis à jour avec succès. Veuillez vous reconnecter.',
          type: 'success'
        });

        setFormData(prev => ({
          ...prev,
          mot_de_passe: ''
        }));

        localStorage.removeItem('token');
      } else {
        setError({
          show: true,
          message: data.message || 'Erreur lors du changement de mot de passe',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      setError({
        show: true,
        message: 'Erreur de connexion au serveur'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Un lien du pied de page : une pop-up (#aide…) ou une adresse externe.
  const ouvrirCible = (cible, evenement) => {
    if (cible?.startsWith('#')) {
      evenement.preventDefault();
      const cle = cible.slice(1);
      if (contenus.popups[cle]) setPopupOuverte(cle);
    }
  };

  // ==========================================================================
  // Rendu
  // ==========================================================================

  // Un champ à libellé intégré, comme sur la maquette validée.
  const classeChamp = 'relative rounded-xl border-[1.5px] border-[color:var(--connexion-principale-15)] bg-white/70 px-3.5 pt-2 pb-1.5 transition focus-within:border-[color:var(--connexion-principale)] focus-within:ring-4 focus-within:ring-[color:var(--connexion-principale-10)]';
  const classeLibelle = 'block text-[10.5px] font-bold uppercase tracking-[0.08em] text-gray-500';
  const classeSaisie = 'w-full border-0 bg-transparent pt-0.5 text-[15px] text-gray-900 outline-none placeholder:text-gray-400';

  // ==========================================================================
  // La seconde étape, dans le même langage que la connexion
  // ==========================================================================
  // Elle prend la place du formulaire, sous le même trait saumon et le même
  // titre : l'utilisateur ne change pas de page, il avance d'une étape. Pas de
  // cadres empilés — le halo fondu reste le seul fond.
  const [codeActif, setCodeActif] = useState(false);

  const renderSecondFacteur = () => {
    const enrolement = etapeDouble.type === 'enrolement';
    const chiffres = codeSaisi.replace(/\s/g, '').slice(0, 6);
    const codeComplet = chiffres.length === 6;

    // Les six cases du code. Une seule vraie saisie, transparente par-dessus :
    // le collage d'un code entier, la saisie automatique du téléphone
    // (`one-time-code`) et l'effacement marchent comme dans un champ normal.
    const casesCode = (
      <div className="relative">
        <div className="grid grid-cols-6 gap-2" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => {
            const courante = codeActif && i === Math.min(chiffres.length, 5);
            return (
              <div
                key={i}
                className={`flex h-14 items-center justify-center rounded-xl border-[1.5px] bg-white/70 font-mono text-2xl font-semibold text-gray-900 transition ${
                  courante
                    ? 'border-[color:var(--connexion-principale)] ring-4 ring-[color:var(--connexion-principale-10)]'
                    : chiffres[i] ? 'border-[color:var(--connexion-principale-40)]' : 'border-[color:var(--connexion-principale-15)]'
                }`}
              >
                {chiffres[i] || ''}
              </div>
            );
          })}
        </div>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          maxLength={6}
          value={chiffres}
          onChange={(e) => setCodeSaisi(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onFocus={() => setCodeActif(true)}
          onBlur={() => setCodeActif(false)}
          aria-label="Code à six chiffres"
          className="absolute inset-0 h-full w-full cursor-text opacity-0"
          required
        />
      </div>
    );

    return (
      <form onSubmit={handleCode} className="grid gap-4">
        <div className="flex items-center gap-2.5 rounded-full bg-white/70 py-1.5 pl-1.5 pr-4 text-sm text-gray-600 ring-1 ring-[color:var(--connexion-principale-10)]">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--connexion-principale)] text-white">
            {enrolement ? <Smartphone className="h-3.5 w-3.5" /> : <ShieldCheck className="h-3.5 w-3.5" />}
          </span>
          <span className="truncate">
            Compte <span className="font-mono font-semibold text-gray-900">{etapeDouble.identifiant}</span>
          </span>
        </div>

        {enrolement && (
          <>
            {/* Trois étapes numérotées, puis le QR : dans l'ordre où on les fait */}
            <ol className="grid gap-2.5 text-sm text-gray-700">
              {[
                "Installez une application d'authentification : Google Authenticator, Microsoft Authenticator…",
                "Dans l'application, ajoutez un compte en scannant ce QR code.",
                "Saisissez le code à six chiffres qu'elle affiche."
              ].map((etape, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#45A89F]/15 text-[11px] font-bold text-[#2f8a82]">
                    {i + 1}
                  </span>
                  <span className="leading-relaxed">{etape}</span>
                </li>
              ))}
            </ol>

            {etapeDouble.qr && (
              <div className="flex flex-col items-center gap-2">
                <div className="rounded-2xl bg-white p-3 shadow-[0_8px_30px_var(--connexion-ombre)] ring-1 ring-[color:var(--connexion-principale-10)]">
                  <img
                    src={etapeDouble.qr}
                    alt="QR code à scanner avec l'application d'authentification"
                    className="h-[196px] w-[196px]"
                  />
                </div>

                {/* La saisie manuelle est le seul recours quand la caméra ne lit rien */}
                {etapeDouble.secret && (
                  <div className="w-full text-center">
                    <button
                      type="button"
                      onClick={() => setSecretVisible((v) => !v)}
                      className="text-xs font-semibold text-[color:var(--connexion-principale)] hover:underline"
                    >
                      {secretVisible ? 'Masquer la clé' : 'Le QR code ne se lit pas ? Afficher la clé à saisir'}
                    </button>
                    {secretVisible && (
                      <p className="mt-2 select-all break-all rounded-[10px] bg-white/80 px-3 py-2 font-mono text-sm tracking-wider text-gray-900 ring-1 ring-[color:var(--connexion-principale-10)]">
                        {(etapeDouble.secret.match(/.{1,4}/g) || []).join(' ')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div>
          <p className={`${classeLibelle} mb-2`}>Code à six chiffres</p>
          {casesCode}
        </div>

        <button
          type="submit"
          disabled={isLoading || !codeComplet}
          className="mt-1 flex w-full items-center justify-center gap-2.5 rounded-xl bg-[color:var(--connexion-principale)] px-4 py-3.5 text-[15px] font-bold text-white transition hover:bg-[color:var(--connexion-foncee)] active:scale-[.99] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isLoading ? (
            <>
              <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" />
              Vérification…
            </>
          ) : (
            <>
              {enrolement ? 'Activer et se connecter' : 'Valider le code'}
              <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.4} />
            </>
          )}
        </button>

        <div className="flex items-center justify-between gap-3 border-t border-[color:var(--connexion-principale-10)] pt-4 text-[13px]">
          <button
            type="button"
            onClick={abandonnerDouble}
            className="inline-flex items-center gap-1.5 font-semibold text-gray-500 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>
          <button
            type="button"
            onClick={() => setPopupOuverte('support')}
            className="font-semibold text-[color:var(--connexion-principale)] hover:underline"
          >
            {enrolement ? 'Téléphone perdu plus tard ?' : "Plus accès à l'application ?"}
          </button>
        </div>
      </form>
    );
  };

  if (showKiosqueWelcome) {
    return <KiosqueWelcomeScreen user={JSON.parse(localStorage.getItem('user'))} />;
  }

  if (showWelcome) {
    return <WelcomeScreen user={JSON.parse(localStorage.getItem('user'))} />;
  }

  // Avant tout formulaire : si l'adresse ne correspond à aucun établissement,
  // il n'y a rien à saisir.
  if (etablissementIndisponible) {
    return (
      <EtablissementIndisponible
        code={etablissementIndisponible}
        sousDomaine={window.location.hostname}
      />
    );
  }

  const etatChoisi = ETATS_ANNEE[etatAnnee(selectedYear)] || null;
  const lignesTitre = String(texteCourant.titre || '').split('\n');

  return (
    <div className="flex min-h-screen flex-col text-gray-900" style={variablesCouleurs(contenus.connexion?.couleurs)}>
      <AlerteConnexion
        ouverte={error.show}
        type={error.type}
        titre={error.titre}
        message={error.message}
        onClose={() => setError({ show: false, message: '' })}
        onSupport={() => {
          // Deux fenêtres Radix fermée et ouverte dans le même rendu laissent
          // la page inerte : on laisse l'alerte se refermer d'abord.
          setError({ show: false, message: '' });
          setTimeout(() => setPopupOuverte('support'), 200);
        }}
      />

      {showPasswordModal && (
        <PasswordChangeModal
          onClose={() => setShowPasswordModal(false)}
          onSubmit={handlePasswordChange}
        />
      )}

      <PopupConnexion
        cle={popupOuverte}
        popup={popupOuverte ? contenus.popups[popupOuverte] : null}
        onClose={() => setPopupOuverte(null)}
      />


      {/* ------------------------------------------------------------------ */}
      {/* Barre du haut : la marque, et le nom de l'établissement du         */}
      {/* sous-domaine                                                       */}
      {/* ------------------------------------------------------------------ */}
      <header className="flex items-center justify-between gap-4 border-b border-[#dbe7ee] bg-white px-4 py-3.5 sm:px-8 lg:px-12">
        {/* Le logo envoyé dans le CIK ; à défaut, le nom et la mention. Pas d'icône. */}
        {contenus.marque.logo ? (
          <img
            src={contenus.marque.logo}
            alt={contenus.marque.nom}
            className="block h-12 w-auto max-w-[60vw] shrink-0 object-contain object-left"
          />
        ) : (
          <div className="min-w-0">
            <p className="text-lg font-extrabold leading-none tracking-[0.12em]">{contenus.marque.nom}</p>
            {contenus.marque.sous_titre && (
              <p className="mt-1 text-[10px] font-medium tracking-[0.08em] text-gray-500">{contenus.marque.sous_titre}</p>
            )}
          </div>
        )}
        <nav className="flex min-w-0 items-center gap-5 text-[13px] font-semibold text-gray-500">
          {nomEcole && <span className="hidden truncate sm:inline">{nomEcole}</span>}
          <button type="button" onClick={() => setPopupOuverte('support')} className="shrink-0 text-[color:var(--connexion-principale)] hover:underline">
            Besoin d'aide ?
          </button>
        </nav>
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Corps : l'affiche à gauche, la connexion fondue à droite           */}
      {/* ------------------------------------------------------------------ */}
      <main className="grid flex-1 gap-8 px-4 py-7 sm:px-8 lg:grid-cols-[minmax(0,1fr)_460px] lg:px-12">
        <section className="relative flex min-h-[400px] flex-col justify-end overflow-hidden rounded-[28px] bg-[color:var(--connexion-affiche)] p-7 text-white sm:min-h-[340px] sm:p-9 lg:min-h-[520px]">
          <SceneEcole className="absolute inset-0 h-full w-full" />
          <div className="absolute inset-0" style={{ backgroundImage: 'var(--connexion-voile)' }} />

          <div className="relative">
            {nomEcole && (
              <p className="mb-3 text-sm font-semibold text-white/80 sm:hidden">{nomEcole}</p>
            )}
            <div className="min-h-[150px]">
              <h1
                style={POLICE_TITRE}
                className={`text-[30px] font-semibold leading-[1.08] transition-all duration-500 sm:text-[40px] lg:text-[52px] ${
                  isAnimating ? '-translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
                }`}
              >
                {lignesTitre.map((ligne, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <br />}
                    {ligne}
                  </React.Fragment>
                ))}
              </h1>
              {texteCourant.sous_titre && (
                <p
                  className={`mt-3 max-w-[540px] leading-relaxed text-white/90 transition-all duration-500 ${
                    isAnimating ? '-translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
                  }`}
                >
                  {texteCourant.sous_titre}
                </p>
              )}
            </div>

            {nombreTextes > 1 && (
              <div className="mt-5 flex gap-1.5" aria-hidden="true">
                {contenus.textes.map((_, index) => (
                  <span
                    key={index}
                    className={`h-1 rounded transition-all duration-300 ${
                      index === currentTextIndex % nombreTextes ? 'w-[42px] bg-white' : 'w-3.5 bg-white/35'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* La connexion n'a pas de bord net : un halo clair, dense au centre,
            s'efface dans le fond de page. L'affiche reste le seul cadre. */}
        <section className="relative isolate self-center px-1 py-2 sm:px-6">
          <div
            aria-hidden="true"
            className="absolute -inset-7 -z-10 rounded-[48px] blur-[14px]"
            style={{
              background: 'radial-gradient(120% 90% at 50% 45%, rgba(255,255,255,.92) 0%, rgba(255,255,255,.7) 55%, rgba(255,255,255,0) 100%)'
            }}
          />

          <span className="mb-3.5 block h-[3px] w-[52px] rounded bg-[#C06B58]" />
          {etapeDouble ? (
            <>
              <h2 className="text-[26px] font-bold">
                {etapeDouble.type === 'enrolement' ? 'Protégez votre compte' : 'Vérification en deux étapes'}
              </h2>
              <p className="mb-5 mt-1 text-sm text-gray-500">
                {etapeDouble.type === 'enrolement'
                  ? "Votre établissement exige la double authentification. Cette étape ne se fait qu'une fois."
                  : "Saisissez le code affiché par votre application d'authentification."}
              </p>
            </>
          ) : (
            <>
              <h2 className="text-[26px] font-bold">{contenus.connexion.titre}</h2>
              {contenus.connexion.sous_titre && (
                <p className="mb-5 mt-1 text-sm text-gray-500">{contenus.connexion.sous_titre}</p>
              )}
            </>
          )}

          {etapeDouble ? renderSecondFacteur() : (
            <form onSubmit={handleSubmit} className="grid gap-3">
              {/* L'année scolaire en boutons : chaque état dit ce qu'il permet */}
              {years.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {years.map((year) => {
                    const etat = ETATS_ANNEE[etatAnnee(year)];
                    const choisie = year.id?.toString() === formData.annee_scolaire_id?.toString();
                    return (
                      <button
                        key={year.id}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, annee_scolaire_id: year.id.toString() }))}
                        aria-pressed={choisie}
                        className={`rounded-xl border-[1.5px] px-2 py-2.5 text-center transition ${
                          choisie
                            ? 'border-[color:var(--connexion-principale)] bg-[color:var(--connexion-pale)] ring-[3px] ring-[color:var(--connexion-principale-10)]'
                            : 'border-[color:var(--connexion-principale-15)] bg-white/60 hover:border-[color:var(--connexion-principale-40)]'
                        }`}
                      >
                        <b className="block text-sm">{year.nom}</b>
                        <small className={`text-[11px] ${choisie ? 'font-semibold text-[color:var(--connexion-principale)]' : 'text-gray-500'}`}>
                          {etat?.libelle}
                        </small>
                      </button>
                    );
                  })}
                </div>
              )}

              {etatChoisi && (
                <p className={`rounded-[10px] px-3 py-2 text-xs ${etatChoisi.classes}`}>{etatChoisi.note}</p>
              )}

              <div className={classeChamp}>
                <label htmlFor="identifiant" className={classeLibelle}>Identifiant</label>
                <input
                  id="identifiant"
                  type="text"
                  name="identifiant"
                  value={formData.identifiant}
                  onChange={handleInputChange}
                  autoComplete="username"
                  className={classeSaisie}
                  required
                  placeholder="prenom.nom"
                />
              </div>

              <div className={classeChamp}>
                <label htmlFor="mot_de_passe" className={classeLibelle}>Mot de passe</label>
                <input
                  id="mot_de_passe"
                  type={showPassword ? 'text' : 'password'}
                  name="mot_de_passe"
                  value={formData.mot_de_passe}
                  onChange={handleInputChange}
                  autoComplete="current-password"
                  className={`${classeSaisie} pr-8`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-[18px] w-[18px]" /> : <Eye className="h-[18px] w-[18px]" />}
                </button>
              </div>

              <div className="flex items-center justify-between text-[13px]">
                <label htmlFor="remember-me" className="flex cursor-pointer select-none items-center gap-2 text-gray-500">
                  <input
                    type="checkbox"
                    id="remember-me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 cursor-pointer accent-[color:var(--connexion-principale)]"
                  />
                  Se souvenir de moi
                </label>
                <button
                  type="button"
                  onClick={() => setPopupOuverte('mot_de_passe')}
                  className="font-semibold text-[color:var(--connexion-principale)] hover:underline"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="mt-1 flex w-full items-center justify-center gap-2.5 rounded-xl bg-[color:var(--connexion-principale)] px-4 py-3.5 text-[15px] font-bold text-white transition hover:bg-[color:var(--connexion-foncee)] active:scale-[.99] disabled:opacity-60"
              >
                {/* Le chargement reste dans le bouton : pas de voile sur la page */}
                {isLoading ? (
                  <>
                    <span className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" />
                    Connexion en cours…
                  </>
                ) : (
                  <>
                    Se connecter
                    <ArrowRight className="h-[18px] w-[18px]" strokeWidth={2.4} />
                  </>
                )}
              </button>

              <p className="border-t border-[color:var(--connexion-principale-10)] pt-4 text-center text-[13px] text-gray-500">
                Besoin d'aide ?{' '}
                <button type="button" onClick={() => setPopupOuverte('support')} className="font-semibold text-[color:var(--connexion-principale)] hover:underline">
                  Contacter le support
                </button>
              </p>
              {/* Les autres portes de l'école, côte à côte (et non plus deux phrases
                  empilées, demande de Daniel du 8/10/2026) : chacune à la couleur
                  de son espace - vert pour les familles, terre cuite pour les
                  enseignants -, sans icône (elle grandissait les tuiles pour rien).
                  Une seule ouverte : elle prend toute la largeur. */}
              {(espaceFamilleOuvert || espaceEnseignantOuvert) && (
                <nav aria-label="Autres espaces">
                  <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">Vous n'êtes pas du personnel ?</p>
                  <div className={`grid gap-2 ${espaceFamilleOuvert && espaceEnseignantOuvert ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {espaceFamilleOuvert && (
                      <a
                        href="/espace-famille/connexion"
                        className="block rounded-xl border border-gray-200 bg-white px-3 py-2 text-center transition hover:border-[#319190] hover:bg-[#319190]/5"
                      >
                        <span className="block truncate text-[13px] font-semibold text-[#319190]">Espace famille</span>
                        <span className="block truncate text-[11px] text-gray-500">Parents d'élèves</span>
                      </a>
                    )}
                    {espaceEnseignantOuvert && (
                      <a
                        href="/espace-enseignant/connexion"
                        className="block rounded-xl border border-gray-200 bg-white px-3 py-2 text-center transition hover:border-[#a9533f] hover:bg-[#a9533f]/5"
                      >
                        <span className="block truncate text-[13px] font-semibold text-[#a9533f]">Espace enseignant</span>
                        <span className="block truncate text-[11px] text-gray-500">Enseignants</span>
                      </a>
                    )}
                  </div>
                </nav>
              )}
            </form>
          )}
        </section>
      </main>

      {/* ------------------------------------------------------------------ */}
      {/* Copyright et pied de page                                          */}
      {/* ------------------------------------------------------------------ */}
      {contenus.pied.copyright && (
        <p className="px-4 pb-2.5 text-center text-xs text-gray-500">
          © {new Date().getFullYear()} {contenus.pied.copyright}
        </p>
      )}
      {contenus.pied.liens.length > 0 && (
        <footer className="flex flex-wrap justify-center gap-x-6 gap-y-1.5 bg-[color:var(--connexion-principale)] px-4 py-3 text-[13px] font-semibold text-white">
          {contenus.pied.liens.map((lien, i) => {
            const externe = !lien.cible?.startsWith('#');
            return (
              <a
                key={`${lien.libelle}-${i}`}
                href={lien.cible}
                onClick={(e) => ouvrirCible(lien.cible, e)}
                {...(externe ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                className="hover:underline"
              >
                {lien.libelle}{externe ? ' ↗' : ''}
              </a>
            );
          })}
        </footer>
      )}
    </div>
  );
};

export default ConnexionPage;
